/* lib/dealers.js — чистые помощники раздела «Дилеры» (#509): сроки, деньги, статусы.
 * Деньги приходят строками с двумя знаками ("13410.00"); считает сервер — здесь только показ. */

import { fmt } from './format.js';

// Кнопки срока: 1 / 3 / 6 / 12 мес. (30 / 90 / 180 / 360 дн., как у сервера TERM_PRESETS).
export const TERM_PRESETS = [
  { days: 30, label: '1 мес.' },
  { days: 90, label: '3 мес.' },
  { days: 180, label: '6 мес.' },
  { days: 360, label: '12 мес.' },
];
export const MAX_DAYS = 3660;

export const GRANT_STATUS = {
  active: { label: 'Действует', tone: 'ok', icon: 'check-circle' },
  cancelled: { label: 'Отменена', tone: 'neutral', icon: 'x-circle' },
};

export const GRANT_MODE = {
  activate: 'включится',
  extend: 'продлит текущий срок',
  replace: 'заменит текущий тариф',
};

/** «13 410,00 ₽» из строки сервера; пусто/нет — «—». */
export const rub = (v) => (v == null || v === '' ? '—' : fmt.money(Number(v)));

/** «20 %» / «25,5 %»; пусто — «—». */
export function pct(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${String(Math.round(n * 100) / 100).replace('.', ',')} %`;
}

/** Срок в днях из поля ввода: целое 1…MAX_DAYS, иначе null (кнопка «Выдать» заблокирована). */
export function parseDays(raw) {
  const s = String(raw ?? '').trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n >= 1 && n <= MAX_DAYS ? n : null;
}

/** «90 дней (3 мес.)» — подпись срока. */
export function termLabel(days) {
  const n = Number(days);
  const preset = TERM_PRESETS.find((p) => p.days === n);
  const base = `${n} ${fmt.plural(n, 'день', 'дня', 'дней')}`;
  return preset ? `${base} (${preset.label})` : base;
}

/** «2026-10» → «октябрь 2026». */
const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август',
  'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
export function monthLabel(ym) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || ''));
  if (!m) return String(ym || '—');
  return `${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

/** Долг дилера словами: «долг 3 560,00 ₽» / «переплата 100,00 ₽» / «расчёты закрыты». */
export function balanceLabel(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return 'расчёты закрыты';
  return n > 0 ? `долг ${rub(n)}` : `переплата ${rub(-n)}`;
}

/** Можно ли отменить выдачу из списка: действует и это последняя по заведению. Окончательно
 *  решает сервер (grant_superseded) — здесь только чтобы не показывать заведомо лишнюю кнопку. */
export function cancellableIds(grants) {
  // последняя НЕотменённая по заведению: отмена новой возвращает заведение на предыдущую —
  // её снова можно отменить (цепочка; окончательно решает сервер — grant_superseded)
  const latest = new Map();
  (grants || []).forEach((g) => {
    const ws = g.workspace && g.workspace.id;
    if (ws == null || g.status !== 'active') return;
    const prev = latest.get(ws);
    if (!prev || new Date(g.created_at) > new Date(prev.created_at)) latest.set(ws, g);
  });
  return new Set([...latest.values()].map((g) => g.id));
}

/** Почему кнопка «Выдать тариф» недоступна — словами (null — доступна). `plans` — тарифы дилера
 *  клиента (`card.dealer_plans`): выдают только их; `partner` — текст для сотрудника дилера. */
export function grantBlockReason(card, plans, { partner = false } = {}) {
  if (!card || !card.dealer) return 'Сначала закрепите клиента за дилером.';
  if (!card.dealer.is_active) return 'Дилер клиента неактивен — выдавать от него нельзя.';
  if (!plans || !plans.length) {
    return partner
      ? 'Вам пока не открыт ни один тариф — обратитесь к менеджеру Ресториума.'
      : 'У дилера нет тарифов для выдачи — их отмечают в карточке дилера (право «Дилеры: настройка»).';
  }
  return null;
}

/** Ответ apiFetch → ошибка с полями, которые читают keyedFailure / isKeyReused (status, code, data). */
export function responseError(r, fallback) {
  const data = (r && r.data) || null;
  const err = new Error((data && data.detail) || fallback);
  err.status = r ? r.status : 0;
  err.data = data;
  err.code = data && data.code;
  err.detail = data && data.detail;
  return err;
}

/* ── отчёт «Заведения» (#528) ────────────────────────────────────────────────────
 * Ключи — байт-в-байт с backend/dealers/reports.py (PLAN_STATUSES, POS_LABELS, ATTENTION) и в
 * том же порядке: зеркало сверяет dealers/tests/test_dealer_reports.py::MirrorTests. */

export const VENUE_PLAN_STATUS = {
  active: { label: 'действует', tone: 'ok', icon: 'check-circle' },
  expiring: { label: 'кончается', tone: 'warn', icon: 'clock' },
  trial: { label: 'пробный период', tone: 'info', icon: 'clock' },
  ended: { label: 'закончился', tone: 'bad', icon: 'x-circle' },
  none: { label: 'без платного тарифа', tone: 'neutral', icon: 'minus' },
};

export const VENUE_POS_STATE = {
  fresh: { label: 'обмен идёт', tone: 'ok', icon: 'check-circle' },
  error: { label: 'ошибка', tone: 'bad', icon: 'alert-circle' },
  stale: { label: 'нет обмена', tone: 'warn', icon: 'clock' },
  needs_point: { label: 'не выбрана точка сети', tone: 'warn', icon: 'alert-triangle' },
  disabled: { label: 'выключена', tone: 'neutral', icon: 'unlink' },
  none: { label: 'не подключена', tone: 'neutral', icon: 'minus' },
};

export const VENUE_ATTENTION = {
  pos_error: 'Касса: ошибка',
  pos_stale: 'Касса: нет обмена больше 48 ч',
  pos_needs_point: 'Касса: не выбрана точка сети',
  plan_expiring: 'Тариф кончается',
  plan_ended: 'Тариф закончился',
  inactive_14d: 'Не заходили 14 дней',
};

/** Строки отчёта: «требуют внимания» и поиск по заведению, email и имени клиента. */
export function filterVenues(rows, { attentionOnly = false, q = '' } = {}) {
  const needle = String(q || '').trim().toLowerCase();
  return (rows || []).filter((r) => (!attentionOnly || (r.attention && r.attention.length > 0))
    && (!needle || [r.workspace && r.workspace.name, r.client && r.client.email, r.client && r.client.full_name]
      .some((s) => String(s || '').toLowerCase().includes(needle))));
}

/** Последняя активность словами: «сегодня» / «вчера» / «5 дней назад»; нет — «нет за 90 дней»
 *  (сервер смотрит присутствие за 90 дней). Дни — по местной полуночи. */
export function activityLabel(iso, now = new Date()) {
  if (!iso) return 'нет за 90 дней';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  const day = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.max(0, Math.round((day(now) - day(at)) / 86400000));
  if (days === 0) return 'сегодня';
  if (days === 1) return 'вчера';
  return `${days} ${fmt.plural(days, 'день', 'дня', 'дней')} назад`;
}

/* ── режим дилера ─────────────────────────────────────────────────────────────── */

/** Сотрудник дилера без наших прав раздела — экран в режиме дилера (наш сотрудник важнее). */
export function isPartnerOnly(user) {
  return !!(user && user.dealer_partner && !user.dealers_manage && !user.dealers_grant);
}

export const INVITE_STATUS = {
  active: { label: 'Ждёт клиента', tone: 'info', icon: 'clock' },
  used: { label: 'Принято', tone: 'ok', icon: 'check-circle' },
  revoked: { label: 'Отозвано', tone: 'neutral', icon: 'x-circle' },
  expired: { label: 'Истекло', tone: 'neutral', icon: 'clock' },
};

/** Ссылка приглашения для клиента: путь страницы подтверждения на нашем домене. */
export function inviteLink(token, origin) {
  const base = String(origin || '').replace(/\/+$/, '');
  return `${base}/partner/invite/${encodeURIComponent(token)}`;
}

/** Лимит долга словами для предпросмотра выдачи (null — лимита нет). */
export function limitText(limit) {
  if (!limit) return null;
  const head = `Лимит долга ${rub(limit.debt_limit_rub)}: сейчас долг ${rub(limit.debt_rub)}, с этой выдачей — ${rub(limit.after_rub)}.`;
  return limit.over ? `${head} Выдача превысит лимит.` : head;
}

/** Сколько роялти ещё можно начислить до лимита долга: ₽ (< 0 — лимит уже превышен) или null
 *  без лимита. Как сервер (services.limit_info): долг после выдачи = баланс + роялти, переплата
 *  (баланс < 0) запас увеличивает. */
export function limitRoom(limitRub, balanceRub) {
  if (limitRub == null || limitRub === '') return null;
  const lim = Number(limitRub);
  const bal = Number(balanceRub) || 0;
  if (!Number.isFinite(lim)) return null;
  return Math.round((lim - bal) * 100) / 100;
}
